import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import {
  type PaymentApplicationFormValue,
  PaymentApplicationRow,
} from '../../../components/bookkeeping/PaymentApplicationRow.js';
import {
  type PaymentWithholdingFormValue,
  PaymentWithholdingRow,
} from '../../../components/bookkeeping/PaymentWithholdingRow.js';
import type {
  Account,
  AccountNode,
  Customer,
  Invoice,
  ReceivePayment as ReceivePaymentType,
  WithholdingRate,
} from '../../../types/bookkeeping.js';
import type { ConfigStatus } from '../../../types/onboarding.js';

const FX_SCALE = 1_000_000_000n;

function flattenTree(nodes: AccountNode[]): Account[] {
  const result: Account[] = [];
  function walk(ns: AccountNode[]) {
    for (const n of ns) {
      result.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

function defaultApplication(): PaymentApplicationFormValue {
  return { invoice_id: '', amount_applied: '' };
}

function defaultWithholding(): PaymentWithholdingFormValue {
  return { invoice_id: '', withholding_rate_id: '', amount: '' };
}

interface ReceivePaymentDetail {
  payment: ReceivePaymentType;
  applications: Array<{ invoice_id: string; amount_applied: string }>;
  withholdings: Array<{ invoice_id: string; withholding_rate_id: string; amount: string }>;
}

export function ReceivePaymentForm() {
  const { t } = useTranslation('bookkeeping');
  const nav = useNavigate();
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  const { id: paymentId } = useParams<{ id: string }>();
  const isNew = !paymentId;

  const today = new Date().toISOString().slice(0, 10);
  const [customerId, setCustomerId] = useState('');
  const [paymentDate, setPaymentDate] = useState(today);
  const [depositAccountId, setDepositAccountId] = useState('');
  const [currency, setCurrency] = useState('');
  const [fxRate, setFxRate] = useState(FX_SCALE.toString());
  const [memo, setMemo] = useState('');
  const [applications, setApplications] = useState<PaymentApplicationFormValue[]>([
    defaultApplication(),
  ]);
  const [withholdings, setWithholdings] = useState<PaymentWithholdingFormValue[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch config for base currency
  const { data: configData } = useQuery({
    queryKey: ['config-status', activeOrgId],
    queryFn: () => api<ConfigStatus>(`/orgs/${activeOrgId}/config-status`),
    enabled: !!activeOrgId,
  });
  const baseCurrency = configData?.organization.baseCurrency ?? 'USD';

  const effectiveCurrency = currency || baseCurrency;
  const sameCurrency = effectiveCurrency === baseCurrency || effectiveCurrency.length !== 3;

  // Auto-fetch fx rate
  const { data: fxData } = useQuery({
    queryKey: ['fx', effectiveCurrency, baseCurrency, paymentDate],
    queryFn: () =>
      api<{ rate: string; source: string }>(
        `/bookkeeping/fx-rates?date=${paymentDate}&from=${effectiveCurrency}&to=${baseCurrency}`,
      ),
    enabled: !!effectiveCurrency && effectiveCurrency.length === 3 && !sameCurrency,
    retry: false,
  });

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['bookkeeping-customers', activeOrgId],
    queryFn: () => api<{ customers: Customer[] }>('/bookkeeping/customers?limit=200'),
    enabled: !!activeOrgId,
  });
  const customers = (customersData?.customers ?? []).filter((c) => c.is_active);

  // Fetch open/partial invoices for the selected customer
  const { data: openInvoicesData } = useQuery({
    queryKey: ['bk-open-invoices', activeOrgId, customerId],
    queryFn: () =>
      api<{ invoices: Invoice[] }>(
        `/bookkeeping/sales/invoices?customer_id=${customerId}&status=open&limit=100`,
      ),
    enabled: !!activeOrgId && !!customerId,
  });
  // Also fetch partial invoices and merge
  const { data: partialInvoicesData } = useQuery({
    queryKey: ['bk-partial-invoices', activeOrgId, customerId],
    queryFn: () =>
      api<{ invoices: Invoice[] }>(
        `/bookkeeping/sales/invoices?customer_id=${customerId}&status=partial&limit=100`,
      ),
    enabled: !!activeOrgId && !!customerId,
  });

  const openInvoices = [
    ...(openInvoicesData?.invoices ?? []),
    ...(partialInvoicesData?.invoices ?? []),
  ];

  // Fetch accounts
  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });
  const allAccounts = flattenTree(accountsData?.tree ?? []);
  const assetAccounts = allAccounts.filter((a) => a.type === 'asset' && a.is_active);

  // Fetch withholding rates
  const { data: withholdingRatesData } = useQuery({
    queryKey: ['bookkeeping-withholding-rates', activeOrgId],
    queryFn: () =>
      api<{ withholding_rates: WithholdingRate[] }>(
        '/bookkeeping/sales/withholding-rates?limit=100',
      ),
    enabled: !!activeOrgId,
  });
  const withholdingRates = (withholdingRatesData?.withholding_rates ?? []).filter(
    (r) => r.is_active,
  );

  // Load existing payment if editing
  useQuery({
    queryKey: ['bk-receive-payment', paymentId],
    queryFn: () => api<ReceivePaymentDetail>(`/bookkeeping/sales/receive-payments/${paymentId}`),
    enabled: !isNew && !!paymentId && !seeded,
    onSuccess: (data: ReceivePaymentDetail) => {
      if (!seeded) {
        setCustomerId(data.payment.customer_id);
        setPaymentDate(data.payment.payment_date);
        setDepositAccountId(data.payment.deposit_account_id);
        setCurrency(data.payment.currency);
        setFxRate(data.payment.fx_rate);
        setMemo(data.payment.memo ?? '');
        setApplications(
          data.applications.length > 0
            ? data.applications.map((a) => ({
                invoice_id: a.invoice_id,
                amount_applied: a.amount_applied,
              }))
            : [defaultApplication()],
        );
        setWithholdings(
          data.withholdings.map((w) => ({
            invoice_id: w.invoice_id,
            withholding_rate_id: w.withholding_rate_id,
            amount: w.amount,
          })),
        );
        setSeeded(true);
      }
    },
  } as Parameters<typeof useQuery>[0]);

  // Live totals
  let grossBig = 0n;
  for (const app of applications) {
    try {
      if (app.amount_applied) grossBig += BigInt(app.amount_applied);
    } catch {
      // ignore
    }
  }

  let withholdingTotalBig = 0n;
  for (const wh of withholdings) {
    try {
      if (wh.amount) withholdingTotalBig += BigInt(wh.amount);
    } catch {
      // ignore
    }
  }

  const netBig = grossBig - withholdingTotalBig;

  // Invoice options for withholdings — only invoices that are in applications
  const invoiceOptionsForWithholdings = applications
    .filter((a) => a.invoice_id)
    .map((a) => {
      const inv = openInvoices.find((i) => i.id === a.invoice_id);
      return inv ? { id: inv.id, number: inv.number } : null;
    })
    .filter(Boolean) as Array<{ id: string; number: string }>;

  const canSubmit =
    !!customerId &&
    !!paymentDate &&
    !!depositAccountId &&
    effectiveCurrency.length === 3 &&
    applications.length >= 1 &&
    applications.every((a) => a.invoice_id && a.amount_applied);

  const submit = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        customer_id: customerId,
        payment_date: paymentDate,
        deposit_account_id: depositAccountId,
        currency: effectiveCurrency,
        fx_rate: sameCurrency ? FX_SCALE.toString() : (fxData?.rate ?? fxRate),
        memo: memo || null,
        applications: applications.map((a) => ({
          invoice_id: a.invoice_id,
          amount_applied: a.amount_applied,
        })),
        withholdings: withholdings.map((w) => ({
          invoice_id: w.invoice_id,
          withholding_rate_id: w.withholding_rate_id,
          amount: w.amount,
        })),
      });
      return isNew
        ? api('/bookkeeping/sales/receive-payments', { method: 'POST', body })
        : api(`/bookkeeping/sales/receive-payments/${paymentId}`, { method: 'PATCH', body });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bk-receive-payments'] });
      if (!isNew) await qc.invalidateQueries({ queryKey: ['bk-receive-payment', paymentId] });
      nav('/app/bookkeeping/sales/receive-payments');
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setSubmitError(err.body?.error ?? err.message ?? 'Error desconocido');
    },
  });

  return (
    <div className="space-y-4">
      {/* Title bar */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {isNew
            ? t('sales.receive_payments.new')
            : t('sales.receive_payments.editTitle', { defaultValue: 'Editar pago recibido' })}
        </h1>
        <button
          type="button"
          onClick={() => nav('/app/bookkeeping/sales/receive-payments')}
          className="rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          {t('journalEntries.cancel')}
        </button>
      </div>

      {/* Header fields */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {/* Customer (required) */}
        <div className="col-span-2 md:col-span-1">
          <label className="block text-xs text-slate-500">
            {t('sales.invoices.customer')} *
            <select
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value);
                // Reset applications when customer changes
                setApplications([defaultApplication()]);
                setWithholdings([]);
              }}
              className="mt-0.5 w-full rounded border px-2 py-1 text-sm"
              required
            >
              <option value="">— {t('sales.invoices.customer')}</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Payment date */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('sales.invoices.entry_date')}
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>

        {/* Deposit account */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('sales.receive_payments.deposit_account')} *
            <select
              value={depositAccountId}
              onChange={(e) => setDepositAccountId(e.target.value)}
              className="mt-0.5 w-full rounded border px-2 py-1 text-sm"
              required
            >
              <option value="">— {t('sales.receive_payments.deposit_account')}</option>
              {assetAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Currency */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('sales.invoices.currency')}
            <input
              value={effectiveCurrency}
              maxLength={3}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm uppercase"
            />
          </label>
        </div>

        {/* FX rate */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('journalEntries.columns.fxRate')}
            <input
              value={sameCurrency ? FX_SCALE.toString() : (fxData?.rate ?? fxRate)}
              disabled={sameCurrency}
              onChange={(e) => setFxRate(e.target.value)}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm disabled:bg-slate-50 disabled:text-slate-400"
            />
          </label>
        </div>

        {/* Memo */}
        <div className="col-span-2 md:col-span-3">
          <label className="block text-xs text-slate-500">
            {t('journalEntries.fields.memo')}
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              rows={2}
              placeholder={t('journalEntries.fields.memoPlaceholder')}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>
      </div>

      {/* Applications section */}
      <div>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">
          {t('sales.receive_payments.applications')}
        </h2>
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2">{t('sales.invoices.title')}</th>
                <th className="px-2 py-2 text-right">
                  {t('sales.invoices.grand_total', { defaultValue: 'Saldo abierto' })}
                </th>
                <th className="px-2 py-2">
                  {t('sales.invoices.paid_amount', { defaultValue: 'Monto aplicado' })}
                </th>
                <th className="px-2 py-2 text-right">
                  {t('sales.receive_payments.net', { defaultValue: 'Restante' })}
                </th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {applications.map((app, i) => (
                <PaymentApplicationRow
                  // biome-ignore lint/suspicious/noArrayIndexKey: payment application rows are ordered form rows with no stable ID
                  key={i}
                  index={i}
                  value={app}
                  openInvoices={openInvoices.map((inv) => ({
                    id: inv.id,
                    number: inv.number,
                    entry_date: inv.entry_date,
                    grand_total: inv.grand_total,
                    paid_amount: inv.paid_amount,
                  }))}
                  canRemove={applications.length > 1}
                  onChange={(v) => {
                    setSubmitError(null);
                    setApplications((as) => as.map((x, j) => (i === j ? v : x)));
                  }}
                  onRemove={() => setApplications((as) => as.filter((_, j) => j !== i))}
                />
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={() => setApplications((as) => [...as, defaultApplication()])}
          className="mt-2 rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          + {t('journalEntries.addLine')}
        </button>
      </div>

      {/* Withholdings section */}
      <div>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">
          {t('sales.receive_payments.withholdings')}
        </h2>
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2">{t('sales.invoices.title')}</th>
                <th className="px-2 py-2">
                  {t('sales.withholding_rates.title', { defaultValue: 'Tasa de retención' })}
                </th>
                <th className="px-2 py-2">{t('sales.line.total')}</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {withholdings.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-3 text-center text-xs text-slate-400">
                    {t('sales.receive_payments.noWithholdings', {
                      defaultValue: 'Sin retenciones',
                    })}
                  </td>
                </tr>
              ) : (
                withholdings.map((wh, i) => (
                  <PaymentWithholdingRow
                    // biome-ignore lint/suspicious/noArrayIndexKey: withholding rows are ordered form rows with no stable ID
                    key={i}
                    index={i}
                    value={wh}
                    invoiceOptions={invoiceOptionsForWithholdings}
                    withholdingRates={withholdingRates}
                    canRemove={true}
                    onChange={(v) => {
                      setSubmitError(null);
                      setWithholdings((ws) => ws.map((x, j) => (i === j ? v : x)));
                    }}
                    onRemove={() => setWithholdings((ws) => ws.filter((_, j) => j !== i))}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={() => setWithholdings((ws) => [...ws, defaultWithholding()])}
          className="mt-2 rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          + {t('journalEntries.addLine')}
        </button>
      </div>

      {/* Footer totals + submit */}
      <div className="rounded border bg-slate-50 px-4 py-3">
        <div className="flex flex-col items-end gap-1 text-sm">
          <div className="flex gap-4">
            <span className="text-slate-500">{t('sales.receive_payments.gross')}:</span>
            <span className="font-mono">
              {grossBig.toString()} {effectiveCurrency}
            </span>
          </div>
          <div className="flex gap-4">
            <span className="text-slate-500">{t('sales.receive_payments.withholding_total')}:</span>
            <span className="font-mono">
              {withholdingTotalBig.toString()} {effectiveCurrency}
            </span>
          </div>
          <div className="flex gap-4 font-medium">
            <span className="text-slate-700">{t('sales.receive_payments.net')}:</span>
            <span className="font-mono">
              {netBig.toString()} {effectiveCurrency}
            </span>
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => nav('/app/bookkeeping/sales/receive-payments')}
            className="rounded border px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
          >
            {t('journalEntries.cancel')}
          </button>
          <button
            type="button"
            disabled={!canSubmit || submit.isPending}
            onClick={() => {
              setSubmitError(null);
              submit.mutate();
            }}
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {submit.isPending ? '…' : t('journalEntries.submit')}
          </button>
        </div>
      </div>

      {submitError && <p className="text-sm text-red-600">{submitError}</p>}
    </div>
  );
}
